import { Collapse, message } from "antd"
import CodeParser from "codice-fiscale-js"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { FaMapMarkerAlt } from "react-icons/fa"
import { useHistory } from "react-router"
import { Link } from "react-router-dom"
import styled from "styled-components"

import {
    USER_AUTH_HEADER,
    createBooking,
    getCanBookSlot,
    lockSlot,
    switchSlot,
    updateBookingBio,
} from "../../../../api"
import { useStateValue } from "../../../_shared/context/AppStateStore"
import useRestTable from "../../../_shared/hooks/usePaginatedRestResource"
import { uniqueFilter } from "../../../_shared/toolbox"
import useForm from "../../hooks/useForm"
import useWindowWidth from "../../hooks/useWindowWidth"
import AppButton from "../AppButton"
import placeholderImageDesktop from "../LoginOverlay/slots_img_desktop.png"
import placeholderImageMobile from "../LoginOverlay/slots_img_mobile.png"
import Heading from "../Typography/Heading"
import Paragraph from "../Typography/Paragraph"
import BookingMetaForm from "./BookingMetaForm"

const { Panel } = Collapse

// ----------------------------------------------------------------------------

function BookingForm(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { slots, eventId, refresh, loginFulfilled, event, agentsAccessCode } =
        props

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ user, token }] = useStateValue()

    const [values, updateOne, updateMany] = useForm({
        birth: user?.fiscalCode ? getFiscalCodeBirthDay() : "2020-01-01",
        isPrivacyAccepted: false,
        isDataToThirdPartiesAccepted: false,
        isImagePolicyAccepted: false,
    })

    const [slotCount, setSlotCount] = useState(5)

    const history = useHistory()

    const lastElementRef = useRef(null)

    const [bookings, fetchBookings] = useRestTable(
        `/bookings/user-bookings/${user?.id}`,
        USER_AUTH_HEADER,
        {
            enabled: [user?.id],
        }
    )

    const foundBooking = useMemo(() => {
        return bookings?.find(
            ({ event: targetEvent }) => targetEvent?.id === eventId
        )
        // eslint-disable-next-line
    }, [bookings, user])

    const hasBooked = foundBooking ? true : false
    const hasManyDays = getUniqueDates()?.length > 1

    const [activePanel, setActivePanel] = useState("")
    const [activeFormSection, setActiveFormSection] = useState("1")

    const vw = useWindowWidth()

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        if (slotCount === 5) return
        lastElementRef.current &&
            lastElementRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            })
        //eslint-disable-next-line
    }, [lastElementRef.current, slotCount])

    useEffect(() => {
        if (foundBooking) {
            const {
                name,
                lastName,
                phone,
                email,
                residence,
                birth,
                slot,
                isPrivacyAccepted,
                isDataToThirdPartiesAccepted,
                isImagePolicyAccepted,
            } = foundBooking
            updateMany({
                name,
                lastName,
                phone,
                email,
                residence,
                birth,
                slot: slot?.id,
                locationCode: slot?.locationCode,
                isPrivacyAccepted: isPrivacyAccepted || false,
                isDataToThirdPartiesAccepted:
                    isDataToThirdPartiesAccepted || false,
                isImagePolicyAccepted: isImagePolicyAccepted || false,
            })
        } else {
            updateMany({
                ...values,
                locationCode: event?.locations?.[0]?.code,
            })
        }
        // eslint-disable-next-line
    }, [foundBooking, event])

    useEffect(() => {
        user?.id && fetchBookings()
        // eslint-disable-next-line
    }, [user?.id])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function getFiscalCodeBirthDay() {
        const codeParser = new CodeParser(user.fiscalCode)
        const dayJsBirth = dayjs(codeParser.birthday)
        return dayJsBirth.format("YYYY-MM-DD")
    }

    function getUniqueDates() {
        return slots?.map((slot) => slot.date).filter(uniqueFilter)
    }

    function handleLoadMore() {
        setSlotCount(slotCount + 5)
    }

    async function handleNextClick() {
        if (!values.slot) {
            message.error("Selezionare uno slot orario per proseguire")
            return
        }

        // ensure slot is free
        const canBookSlot = await getCanBookSlot(values.slot, token)

        if (!canBookSlot) {
            message.error(
                "Ci dispiace, sembra che questo slot non sia più disponibile"
            )
            updateOne("slot", null)
            refresh()
            return
        }

        await lockSlot(values.slot, token)
        // await refresh()

        setActiveFormSection("2")
        document.querySelector("#scroll-target").scrollIntoView({
            block: "start",
            inline: "nearest",
            behavior: "smooth",
        })
    }

    async function handleLockTimeExpired() {
        setActiveFormSection("1")
        updateOne("slot", null)
        await refresh()
        message.error(
            "Ci dispiace, non possiamo bloccare il tuo slot per più di 10 minuti."
        )
    }

    function validateDateFormat(date) {
        const format = "YYYY-MM-DD"
        const pat = /[0-9]{4}-[0-9]{2}-[0-9]{2}/

        if (!pat.test(date) || !(dayjs(date, format).format(format) === date)) {
            throw new Error(
                "La data di nascita deve essere nel formato YYYY-MM-DD"
            )
        }
    }

    function valuesHaveChanged(newBody) {
        let differenceWasFound = false

        Object.entries(newBody).forEach(([key, value]) => {
            const otherValue = foundBooking?.[key]
            if (value !== otherValue) {
                differenceWasFound = true
            }
        })
        return differenceWasFound
    }

    async function handleEdit(e) {
        e.preventDefault()
        try {
            if (!validateEmail(values?.email)) {
                return message.error("Indirizzo email non valido.")
            }

            // check that privacy policy is accepted
            if (!values.isPrivacyAccepted) {
                return message.error(
                    "Devi accettare la Privacy Policy per proseguire con la prenotazione."
                )
            }

            validateDateFormat(values?.birth)
            const newBookingBody = {
                birth: values.birth,
                name: values.name,
                lastName: values.lastName,
                phone: values.phone,
                email: values.email,
                residence: values.residence,
                isPrivacyAccepted: values.isPrivacyAccepted,
                isDataToThirdPartiesAccepted:
                    values.isDataToThirdPartiesAccepted,
                isImagePolicyAccepted: values.isImagePolicyAccepted,
            }

            foundBooking.slot?.id !== values.slot &&
                (await handleSwitchBooking())
            valuesHaveChanged(newBookingBody) &&
                (await updateBookingBio(newBookingBody, foundBooking.id, {
                    userId: user.id,
                }))
            history.push("/booking/edit")
        } catch (e) {
            message.error(e.message)
        }
    }

    async function handleSwitchBooking() {
        try {
            await switchSlot(foundBooking.slot.id, values.slot)
            message.success(
                "La tua prenotazione è stata modificata con successo."
            )
            fetchBookings()
            refresh()
        } catch (e) {
            if (e.statusCode === 409) {
                throw new Error("Questa fascia oraria è già prenotata")
            } else {
                throw new Error("Errore durante il cambio di fascia oraria")
            }
        }
    }

    function validateEmail(email) {
        const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return re.test(String(email).toLowerCase())
    }

    async function redirectToProviderForm() {
        // will create booking-ghost instead of booking
        const { url } = await createBooking(
            eventId,
            {
                ...values,
                agentsAccessCode,
            },
            token
        )

        window.location = url
    }

    async function handleSubmit(e) {
        e.preventDefault()

        if (event?.eventType?.requiresProviderForm) {
            redirectToProviderForm()
            return
        }

        if (!validateEmail(values?.email)) {
            return message.error("Indirizzo email non valido.")
        }

        // check that slot was selected
        if (!values.slot) {
            message.error(
                "Devi selezionare uno slot orario per effettuare la prenotazione."
            )
            return
        }

        //check that date is valid
        const date = new Date(values.birth)
        if (isNaN(date.getTime())) {
            message.error(
                "Controlla di aver inserito una data di nascita valida."
            )
            return
        }

        // check that privacy policy is accepted
        if (!values.isPrivacyAccepted) {
            message.error(
                "Devi accettare la Privacy Policy per proseguire con la prenotazione."
            )
            return
        }
        try {
            validateDateFormat(values?.birth)

            if (event?.eventType?.requiresProviderForm) {
                redirectToProviderForm()
                return
            }

            await createBooking(
                eventId,
                {
                    ...values,
                    agentsAccessCode,
                },
                token
            )
            history.push("/booking")
        } catch (e) {
            message.error(e.message)
        }
    }

    function renderLocationSelector() {
        return (
            <div className="location">
                {event.locations?.map((l) => {
                    return (
                        <AppButton
                            key={l.code}
                            uxType="primary"
                            htmlType="button"
                            className={`location-btn ${
                                values.locationCode === l.code ? "active" : ""
                            }`}
                            onClick={() => {
                                if (values.locationCode !== l.code) {
                                    updateMany({
                                        ...values,
                                        slot: null,
                                        locationCode: l.code,
                                    })
                                }
                            }}
                        >
                            {l.address}, {l.city}
                        </AppButton>
                    )
                })}
            </div>
        )
    }

    function isActive(id) {
        return id === values.slot ? "active" : ""
    }

    function handleSlotSelect(id) {
        return () => {
            const fv = { ...values }
            fv.slot = id
            updateMany(fv)
        }
    }

    function renderItem(window, id, date, locationCode, eventType, index, max) {
        const location = event?.locations?.find((l) => l.code === locationCode)
        return (
            <div
                key={id}
                className="slot"
                ref={
                    index === max - 1 || index === slots?.length - 1
                        ? lastElementRef
                        : undefined
                }
            >
                <div className="slot-content">
                    <div className="info">
                        {event?.isHybrid && eventType && (
                            <span className="event-type">{eventType.name}</span>
                        )}
                        <span className="location">
                            <FaMapMarkerAlt /> {location.address},{" "}
                            {location.city}
                        </span>
                        <span className="date">
                            {dayjs(date).format("DD MMMM YYYY")}
                        </span>
                        <span className="window">{window}</span>
                    </div>
                    <AppButton
                        uxType="primary"
                        htmlType="button"
                        className={`slot-btn ${isActive(id)}`}
                        onClick={handleSlotSelect(id)}
                    >
                        {isActive(id) ? "Selezionato" : "Seleziona"}
                    </AppButton>
                </div>
                <div className="divider" />
            </div>
        )
    }

    function renderSlots(localSlots) {
        return (
            <>
                <div className="slot-list">
                    {localSlots
                        ?.slice(0, slotCount)
                        ?.map((slot, index) =>
                            renderItem(
                                slot?.window,
                                slot?.id,
                                slot?.date,
                                slot?.locationCode,
                                slot?.eventType,
                                index,
                                slotCount
                            )
                        )}
                </div>
            </>
        )
    }

    function handleActivePanelChange(e) {
        const newActive = e.find((key) => key !== activePanel)
        setActivePanel(newActive)
        setSlotCount(5)
        // scrollToCollapseStart()
    }

    function renderCollapsedSlots() {
        return (
            <Collapse
                className="slot-collapse"
                ghost
                onChange={handleActivePanelChange}
                activeKey={activePanel}
                destroyInactivePanel={true}
            >
                {getUniqueDates().map((date, index) => (
                    <Panel
                        key={`${index}`}
                        header={dayjs(date).format("DD MMMM YYYY")}
                    >
                        {renderSlots(
                            slots.filter(
                                (slot) =>
                                    slot.date === date &&
                                    slot.locationCode === values.locationCode
                            )
                        )}

                        <div className="buttons">
                            {slotCount <= slots?.length && (
                                <AppButton
                                    className="slot-increment"
                                    uxType="primary"
                                    htmlType="button"
                                    onClick={handleLoadMore}
                                >
                                    Carica altri
                                </AppButton>
                            )}
                            {hasBooked &&
                                foundBooking?.slot?.id !== values.slot && (
                                    <AppButton
                                        className="confirm-switch"
                                        uxType="primary"
                                        htmlType="button"
                                        onClick={handleSwitchBooking}
                                    >
                                        Conferma cambio slot
                                    </AppButton>
                                )}
                        </div>
                    </Panel>
                ))}
            </Collapse>
        )
    }

    function renderCard() {
        return (
            <BookingMetaForm
                foundBooking={foundBooking}
                values={values}
                onChange={updateOne}
                slot={slots?.find((s) => s.id === values.slot)}
                onTimeExpired={handleLockTimeExpired}
                lockBirth={!!user?.fiscalCode}
            />
        )
    }

    function renderSlotSection() {
        return (
            <>
                <Heading boldness="bold" className="slots-heading">
                    Luogo della visita
                </Heading>

                {renderLocationSelector()}

                <Heading boldness="bold" className="slots-heading">
                    Slot orari disponibili
                </Heading>

                {hasManyDays
                    ? renderCollapsedSlots()
                    : renderSlots(
                          slots?.filter(
                              (slot) =>
                                  slot.locationCode === values.locationCode
                          )
                      )}

                <div className="buttons">
                    {slotCount <= slots?.length && !hasManyDays && (
                        <AppButton
                            className="slot-increment"
                            uxType="primary"
                            htmlType="button"
                            onClick={handleLoadMore}
                        >
                            Carica altri
                        </AppButton>
                    )}
                    {hasBooked &&
                        !hasManyDays &&
                        foundBooking?.slot?.id !== values.slot && (
                            <AppButton
                                className="confirm-switch"
                                uxType="primary"
                                htmlType="button"
                                onClick={handleSwitchBooking}
                            >
                                Conferma cambio slot
                            </AppButton>
                        )}
                    <AppButton
                        className={`confirm-switch ${
                            !(slotCount <= slots?.length && !hasManyDays)
                                ? "nomargin"
                                : ""
                        }`}
                        uxType="primary"
                        htmlType="button"
                        onClick={handleNextClick}
                    >
                        Prosegui
                    </AppButton>
                </div>
            </>
        )
    }

    function renderDataSection() {
        return (
            <>
                <Heading boldness="bold" className="form-heading">
                    Inserisci i dati per completare la prenotazione
                </Heading>
                {renderCard()}
            </>
        )
    }
    // -------------------------------------
    // Component local variables
    // -------------------------------------

    let formSection

    if (hasBooked) {
        formSection = (
            <>
                {activeFormSection === "1" && renderSlotSection()}
                {activeFormSection === "2" && renderDataSection()}
            </>
        )
    } else if (loginFulfilled) {
        formSection = (
            <>
                {activeFormSection === "1" && renderSlotSection()}
                {activeFormSection === "2" && renderDataSection()}
            </>
        )
    } else {
        formSection =
            vw <= 786 ? (
                <img
                    className="placeholder-img"
                    src={placeholderImageMobile}
                ></img>
            ) : (
                <img
                    className="placeholder-img"
                    src={placeholderImageDesktop}
                ></img>
            )
    }

    return (
        <form
            onSubmit={foundBooking ? handleEdit : handleSubmit}
            className={props.className}
        >
            <div id="scroll-target"></div>
            {hasBooked && (
                <Paragraph className="booked-paragraph">
                    Hai già effettuato una prenotazione per questo evento. Se
                    vuoi cancellare la tua prenotazione, vai alla sezione{" "}
                    <Link to="/bookings">Biglietti</Link>. Se vuoi cambiare slot
                    orario o modificare i dati della tua prenotazione prosegui
                    su questo modulo.
                </Paragraph>
            )}

            {formSection}
        </form>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

BookingForm.propTypes = {
    className: PropTypes.string.isRequired,
    onSubmit: PropTypes.func,
    slots: PropTypes.array,
    eventId: PropTypes.string,
    date: PropTypes.string,
    defaultBirth: PropTypes.string,
    hasBooked: PropTypes.bool,
    refresh: PropTypes.func,
    event: PropTypes.object,
    loginFulfilled: PropTypes.bool,
    agentsAccessCode: PropTypes.string,
}

BookingForm.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledBookingForm = styled(BookingForm)`
    & {
        display: flex;
        flex-direction: column;
        align-items: center;

        .placeholder-img {
            object-fit: contain;
            width: 100%;
            height: 800px;
            display: block;
        }

        .slot-collapse {
            width: 100%;
            margin-bottom: 20px;
            .slot-increment {
                margin-bottom: 20px;
            }
            .ant-collapse-header {
                display: flex;
                align-items: center;
                justify-content: center;
                font-family: Roboto-Bold;
                font-size: 22px;
                padding: 10px 20px;
                .ant-collapse-arrow {
                    position: static;
                    margin: 0;
                    padding: 0;
                    margin-right: 10px;
                    svg {
                        width: 18px;
                        height: 18px;
                    }
                }
            }
        }
        .booked-paragraph {
            a {
                color: ${({ theme }) => theme.colors.textMuted};
                text-decoration: underline;
            }
            text-align: center;
            margin-bottom: 40px;
        }
        .slots-heading {
            margin-top: 80px;
            margin-bottom: 40px;
            font-size: 32px;
        }
        .buttons {
            display: flex;
            flex-direction: row;
            justify-content: center;
            .slot-increment {
                background-color: transparent !important;
                color: ${({ theme }) =>
                    theme.colors.buttonBackground} !important;
                border: 2px solid
                    ${({ theme }) => theme.colors.buttonBackground} !important;
            }
            .confirm-switch {
                margin-left: 20px;
                &.nomargin {
                    margin-left: 0;
                }
            }
        }

        .location {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            overflow: hidden;
            z-index: 0;

            .location-btn {
                transition: all 0.3s ease;
                width: 400px;
                max-width: 100%;
                &.active {
                    background-color: ${({ theme }) => theme.colors.success};
                }
            }
        }
        .slot-list {
            width: 100%;
            .slot {
                display: flex;
                flex-direction: column;
                align-items: center;
                .slot-content {
                    width: 80%;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    .info {
                        display: flex;
                        flex-direction: column;
                        gap: 1px;

                        span {
                            display: block;
                        }
                        .date {
                            font-family: "Poppins-Regular";
                            color: ${({ theme }) => theme.colors.textMuted};
                            font-size: 16px;
                        }
                        .window {
                            font-family: "Poppins-Bold";
                            font-size: 18px;
                        }
                        .location {
                            font-family: "Poppins-Regular";
                            color: ${({ theme }) => theme.colors.textMuted};
                            font-size: 12px;
                        }
                        .event-type {
                            font-family: "Poppins-Bold";
                            color: ${({ theme }) => theme.colors.textMuted};
                            font-size: 12px;
                        }
                    }
                    .slot-btn {
                        transition: all 0.3s ease;
                        width: 190px;
                        &.active {
                            background-color: ${({ theme }) =>
                                theme.colors.success};
                        }
                    }
                }
                .divider {
                    width: 100%;
                    height: 1px;
                    background-color: ${({ theme }) =>
                        theme.colors.dividerColor};
                    margin: 33px 0px;
                }
            }
        }
        .form-heading {
            margin-top: 150px;
            margin-bottom: 80px;
            font-size: 32px;
        }
        @media (max-width: 980px) {
            padding: 0px 10px !important;
            h2 {
                width: 100%;
                text-align: center;
            }
            .slot-list {
                .slot {
                    .slot-content {
                        width: 100%;
                    }
                }
            }
        }
        @media (max-width: 768px) {
            .buttons {
                flex-direction: column;
                align-items: center;
                .confirm-switch {
                    margin-left: 0px;
                    margin-top: 20px;
                }
            }
        }
        @media (max-width: 576px) {
            .form-heading {
                margin-top: 150px;
                margin-bottom: 40px;
                font-size: 24px;
            }
            .slot-list {
                .slot {
                    .slot-content {
                        .info {
                            span {
                            }
                            .location {
                                max-width: 150px;
                            }
                            .date {
                                font-size: 13px;
                            }
                            .window {
                                font-size: 14px;
                            }
                        }
                        .slot-btn {
                            width: 120px;
                            min-width: 120px;
                            font-size: 13px;
                            height: 40px;
                            padding: 0;
                        }
                    }
                }
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default StyledBookingForm
